/**
 * What a contact needs to be reachable, authored once.
 *
 * A phone is the only guaranteed way to reach either end of a shipment — we don't require an email —
 * and carriers phone ahead to arrange pickup and delivery, so a bad number doesn't fail at booking,
 * it fails at the dock.
 *
 * These rules used to live in two frontends that disagreed: the dashboard checked ten digits and a
 * leading 1, the Shopify embedded app only checked non-empty, so it would happily book a shipment
 * whose delivery phone was "1" (ENG-6448).
 *
 * Deliberately React-free and source-only, which is what lets every frontend consume it without
 * changing package manager: the dashboard and the BigCommerce app via `workspace:*`, the Shopify
 * app — an npm project outside this workspace — via a `file:` dependency.
 *
 * Returns *what* is wrong, never the words for it. Each surface owns its own copy: the dashboard says
 * "Required", the Shopify form says "Contact phone is required", and neither has to agree on wording
 * to agree on the rule.
 */
export type PhoneProblem =
  | "required"
  | "tooShort"
  | "leadingOne"
  | "implausible";

/** Ten digits and no leading 1, ignoring punctuation. */
const DIGITS_REQUIRED = 10;

/**
 * A number nobody answers. These are what people type to get past a required field, and the
 * shipment then leaves with a phone the carrier can't use.
 *
 * The dashboard kept a hardcoded list of twenty. It's a pattern, not data: every entry was either
 * one digit ten times or a two-digit pair five times, and the one exception (1234567890) is already
 * caught by the leading-1 check. Saying it as the pattern means there is no list to keep in step
 * with the backend's copy of this rule — which is the whole point of ENG-6448 — and it also catches
 * the ones the list missed, like 5050505050.
 */
function isObviouslyPlaceholder(digits: string): boolean {
  // The last ten, so a number carrying a country code is judged on the part that identifies the
  // line. Matches the backend's copy of this check exactly.
  const lastTen = digits.slice(-DIGITS_REQUIRED);
  if (lastTen.split("").every((digit) => digit === lastTen[0])) return true;
  return lastTen === lastTen.slice(0, 2).repeat(DIGITS_REQUIRED / 2);
}

/**
 * What's wrong with a phone number, or undefined if nothing is.
 *
 * The number this validates is the number a carrier receives. Only one of the 57 shipment bookers
 * strips non-digits before sending it, so whatever is stored is what almost every carrier gets —
 * which is why the shape is checked here rather than left to be cleaned up later.
 *
 * Punctuation is ignored, so "(604) 555-0134" passes where the old ten-digit regex refused it, and
 * ten digits is a floor rather than an exact count so an extension survives.
 *
 * **A leading 1 is still refused, including "+1 …".** NANP area codes never start with 1, so a bare
 * ten-digit number beginning with one is not a real number; and since nothing downstream drops a
 * country code, "+1 780 463 9444" would reach a carrier with the prefix attached. Merchants whose
 * stored phone looks like that have to type the bare ten digits — a recoverable form error, and
 * cheaper than a booking that fails at the carrier. Numbers with a non-NANP country code are not
 * refused today, only because they can't be told apart from an extension by digit count alone.
 */
export function phoneProblem(
  phoneNumber: string | undefined,
): PhoneProblem | undefined {
  const digits = (phoneNumber ?? "").replace(/\D/g, "");

  if (digits === "") return "required";
  if (digits.length < DIGITS_REQUIRED) return "tooShort";
  if (digits.startsWith("1")) return "leadingOne";
  if (isObviouslyPlaceholder(digits)) return "implausible";

  return undefined;
}
